import axios from 'axios';
import { DATAMART_GLOBAL } from 'Components/Misc/CaseDetails_System';

export type PaymentOutcome = 'successful' | 'failed' | 'cancelled' | 'pending';

export interface StripeConfig {
  enabled: boolean;
  mode: 'test' | 'live';
  supportedCurrencies: string[];
  publishableKey: string | null;
}

export interface StripeLineItem {
  name: string;
  amount: number;
  quantity: number;
}

export interface CreateCheckoutSessionPayload {
  cartTotal?: number;
  customerEmail: string;
  currency?: string;
  lineItems?: StripeLineItem[];
  metadata?: Record<string, string>;
}

export interface CreateCheckoutSessionResponse {
  sessionId: string;
  url: string;
}

export interface SessionStatusResponse {
  sessionId: string;
  offerId?: string | null;
  outcome: PaymentOutcome;
  paymentStatus: string;
  sessionStatus: string;
  customerEmail: string | null;
  amountTotal: number | null;
  currency: string | null;
  createdAt: number;
  metadata?: Record<string, string>;
}

export interface DatamartApiResponse<T> {
  status: boolean;
  data: T;
  errorMessage?: string;
  errorCode?: string;
  requestId?: string;
}

class StripePaymentAPI {
  userID?: string;

  init({ userID }: { userID: string }) {
    if (userID) {
      this.userID = userID;
    } else {
      return { msg: 'Failed to initiate Stripe payment service.' };
    }
  }

  private getHeaders() {
    return {
      'Content-Type': 'application/json; charset=UTF-8',
      'x-user-id': this.userID,
    };
  }

  private buildUrl(endpoint: string, sessionId?: string) {
    const base = `${DATAMART_GLOBAL.baseUrl}${endpoint}`;
    return sessionId ? `${base}/${sessionId}` : base;
  }

  getStripeConfig = async (): Promise<DatamartApiResponse<StripeConfig>> => {
    const res = await axios({
      method: 'get',
      url: this.buildUrl(DATAMART_GLOBAL.endPoints.stripeConfig),
      headers: this.getHeaders(),
    });
    return res.data;
  };

  createCheckoutSession = async (
    payload: CreateCheckoutSessionPayload,
  ): Promise<DatamartApiResponse<CreateCheckoutSessionResponse>> => {
    const res = await axios({
      method: 'post',
      url: this.buildUrl(DATAMART_GLOBAL.endPoints.stripeCheckoutSession),
      headers: this.getHeaders(),
      data: payload,
    });
    return res.data;
  };

  getSessionStatus = async (
    sessionId: string,
    redirectHint?: 'success' | 'cancelled' | string | null,
  ): Promise<DatamartApiResponse<SessionStatusResponse>> => {
    const res = await axios({
      method: 'get',
      url: this.buildUrl(DATAMART_GLOBAL.endPoints.stripeSessionStatus, sessionId),
      headers: this.getHeaders(),
      params: redirectHint ? { redirectHint } : undefined,
    });
    return res.data;
  };
}

const STRIPE_PAYMENT = new StripePaymentAPI();
export default STRIPE_PAYMENT;
